import React, { useCallback, useContext, useState } from 'react';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import { MdClose } from 'react-icons/md';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';
import { amplitudeInstance } from '../../config/amplitude';
import AuthContext from '../../context/AuthContext';
import api from '../../services/api';
import { Metadata, UserList } from '../../types/types';
import CreateListButton from '../CreateListButton';
import CreateListModal from '../CreateListModal';
import UserListBasicItem from '../UserListBasicItem';
interface PropTypes {
  isOpen: boolean;
  onClose: () => void;
  data: {
    contentId: string;
    contentType: string;
  };
}
function ManageListsItemsModal({
  isOpen,
  onClose,
  data
}: PropTypes) {
  const auth = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [isCreateListModalVisible, setIsCreateListModalVisible] = useState(false);
  const t = useTranslations();
  const addItemsToList = useMutation({
    mutationFn: async ({
      listId,
      contentId,
      contentType
    }: {
      listId: string;
      contentId: string;
      contentType: string;
    }) => {
      const response = await api.post(`/v1/me/lists/${listId}/items`, {
        items: [{
          id: contentId,
          type: contentType
        }]
      });
      return response.data;
    },
    onSuccess: (_, {
      listId: listId_0
    }) => {
      toast(t('modals.manage_lists_items.messages.succesfully_added_item.title'), {
        position: 'bottom-center',
        type: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: [`lists-${listId_0}-items-with-pagination`]
      });
      queryClient.invalidateQueries({
        queryKey: ['me-lists-with-pagination']
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
    },
    onError: (err, __0, rollback: any) => {
      toast(t('modals.manage_lists_items.messages.error_adding_item.title'), {
        position: 'bottom-center',
        type: 'error'
      });
    }
  });
  const listsQuery = useInfiniteQuery<{
    data: UserList[];
    metadata: Metadata;
  }>({
    queryKey: [`me-lists-with-pagination`],
    queryFn: async ({
      pageParam = 0
    }) => {
      const offset = pageParam as number || 0;
      const response_0 = await api.get(`/v1/me/lists?offset=${offset}&limit=30`);
      return response_0.data;
    },
    initialPageParam: 0,
    getNextPageParam: lastGroup => {
      if (lastGroup.metadata.total > lastGroup.metadata.offset) {
        return lastGroup.metadata.offset + 20;
      }
      return undefined;
    }
  });
  const onEndReached = useCallback(() => {
    if (listsQuery.hasNextPage && !listsQuery.isFetchingNextPage) {
      listsQuery.fetchNextPage();
    }
  }, [listsQuery.hasNextPage, listsQuery.isFetchingNextPage]);
  const totalItems = listsQuery.data?.pages.map(group => {
    return group.data.length;
  }).reduce(function (a, b) {
    return a + b;
  }, 0) || 0;
  return <Dialog.Root open={isOpen} onOpenChange={isOpen_0 => {
    if (!isOpen_0) {
      onClose();
    }
  }}>
      <CreateListModal isOpen={isCreateListModalVisible} onClose={() => {
      setIsCreateListModalVisible(false);
      amplitudeInstance.logEvent('Close Create List Modal Opened From Manage Lists Items Modal', {
        content_id: data.contentId,
        content_type: data.contentType
      });
    }} />
      <Dialog.Portal>
        <Dialog.Overlay className="flex justify-center items-center fixed inset-0 bg-[rgba(0,0,0,0.7)] bg-opacity-25 transition-opacity z-10 px-2">
          <Dialog.Content className="flex flex-col w-full max-h-[80%] md:w-[500px] md:h-[500px] bg-gray-600 rounded-md p-4">
            <div className="flex flex-col pb-2">
              <div className="flex flex-row justify-between items-center">
                <span className="text-white font-bold text-2xl">
                  {t('modals.manage_lists_items.title')}
                </span>
                <button className="text-white" onClick={onClose}>
                  <MdClose size={28} />
                </button>
              </div>

              <CreateListButton onClick={() => {
              setIsCreateListModalVisible(true);
              amplitudeInstance.logEvent('Open Create List Modal From Manage Lists Items Modal', {
                content_id: data.contentId,
                content_type: data.contentType
              });
            }} className="self-start mt-2" />
            </div>

            <div className="flex flex-col h-full text-white overflow-hidden">
              <BottomScrollListener onBottom={onEndReached} offset={150}>
                {(scrollRef: any) => <div ref={scrollRef} className="flex flex-col overflow-y-auto">
                    {(listsQuery.data?.pages.reduce((acc, val) => acc.concat(val.data as any), []) as UserList[]).map((item, index) => <UserListBasicItem key={item._id} data={item} onClick={() => addItemsToList.mutate({
                  listId: item._id,
                  contentId: data.contentId,
                  contentType: data.contentType
                })} className={`${index === totalItems - 1 ? 'mb-0' : 'mb-2'}`} />)}
                  </div>}
              </BottomScrollListener>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>;
}
export default ManageListsItemsModal;